import { Component, computed, input, model, output, signal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Dialog } from 'primeng/dialog';
import { RadioButton } from 'primeng/radiobutton';
import { ToggleSwitch } from 'primeng/toggleswitch';
import {
  DateOverrideDto,
  IntervalDto,
} from '../../../../infrastructure/shared/model/fss.api';
import {
  ButtonComponent,
  ButtonSeverity,
  ButtonSize,
} from '../../../../infrastructure/shared/ui/button/button';
import { FieldComponent } from '../../../../infrastructure/shared/ui/field/field';
import { CalendarComponent } from '../../../../infrastructure/shared/ui/calendar/calendar';
import { CalendarMinus02 } from '../../../../infrastructure/shared/icons/calendar-minus-02/calendar-minus-02';
import { XClose } from '../../../../infrastructure/shared/icons/x-close/x-close';

type Tip = 'single' | 'period';

@Component({
  standalone: true,
  selector: 'app-date-override-dialog',
  imports: [
    Dialog,
    RadioButton,
    ToggleSwitch,
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    FieldComponent,
    CalendarComponent,
    CalendarMinus02,
    XClose,
  ],
  templateUrl: './date-override-dialog.html',
  styleUrl: './date-override-dialog.scss',
})
export class DateOverrideDialog {
  isVisible = model.required<boolean>();
  existingOverrides = input<DateOverrideDto[]>([]);
  submitted = output<DateOverrideDto[]>();

  protected readonly ButtonSeverity = ButtonSeverity;
  protected readonly ButtonSize = ButtonSize;

  protected tip = signal<Tip>('single');
  protected ceoDan = signal<boolean>(true);

  protected singleDateCtrl = new FormControl<Date | null>(null);
  protected rangeCtrl = new FormControl<Date[] | null>(null);
  protected fromCtrl = new FormControl<Date | null>(null);
  protected toCtrl = new FormControl<Date | null>(null);

  protected unavailableDays = computed(() =>
    this.existingOverrides()
      .map((o) => (o.date ? new Date(o.date) : null))
      .filter((d): d is Date => d !== null),
  );

  protected onTipChange(tip: Tip): void {
    this.tip.set(tip);
    this.ceoDan.set(true);
    this.singleDateCtrl.reset();
    this.rangeCtrl.reset();
    this.fromCtrl.reset();
    this.toCtrl.reset();
  }

  protected onCancel(): void {
    this._reset();
    this.isVisible.set(false);
  }

  protected onSubmit(): void {
    const intervals = this._buildIntervals();
    if (intervals === null) return;

    const dates = this._collectDates();
    if (dates.length === 0) return;

    const existing = new Set(
      this.existingOverrides()
        .map((o) => (o.date ? this._dateKey(new Date(o.date)) : null))
        .filter((k): k is string => k !== null),
    );

    const overrides = dates
      .filter((d) => !existing.has(this._dateKey(d)))
      .map((date) => new DateOverrideDto({ date, intervals }));

    if (overrides.length > 0) {
      this.submitted.emit(overrides);
    }
    this._reset();
    this.isVisible.set(false);
  }

  private _buildIntervals(): IntervalDto[] | null {
    if (this.ceoDan()) return [];
    const from = this.fromCtrl.value;
    const to = this.toCtrl.value;
    if (!from || !to) return null;
    return [
      new IntervalDto({ from: this._toTime(from), to: this._toTime(to) }),
    ];
  }

  private _collectDates(): Date[] {
    if (this.tip() === 'single') {
      const d = this.singleDateCtrl.value;
      return d ? [this._stripTime(d)] : [];
    }
    const range = this.rangeCtrl.value;
    if (!range || range.length < 2 || !range[0] || !range[1]) return [];
    const start = this._stripTime(range[0]);
    const end = this._stripTime(range[1]);
    const out: Date[] = [];
    const cursor = new Date(start);
    while (cursor <= end) {
      out.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return out;
  }

  private _reset(): void {
    this.tip.set('single');
    this.ceoDan.set(true);
    this.singleDateCtrl.reset();
    this.rangeCtrl.reset();
    this.fromCtrl.reset();
    this.toCtrl.reset();
  }

  private _toTime(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }

  private _stripTime(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  private _dateKey(d: Date): string {
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  }
}
